import { HowToSection, QuantitativeValue, Recipe } from 'schema-dts';
import { removeEmpty } from 'src/app/@utils/remove-empty.helper';

import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { SchemaService } from '@core/services/schema.service';
import {
    RecipeIngredientSection,
    RecipeInstructionItem,
    RecipeInstructionSection,
    RecipeSpot,
    RecipeTime
} from '@features/spots/umbraco-block-list-spot/block-list-spot.models';
import { UmbracoImage } from '@models/umbraco.model';
import { RecipeStepsComponent } from '../recipe-steps/recipe-steps.component';
import { RecipeIngredientsComponent } from '../recipe-ingredients/recipe-ingredients.component';
import { RecipeHeaderComponent } from '../recipe-header/recipe-header.component';

@Component({
    selector: 'app-recipe-spot',
    templateUrl: './recipe-spot.component.html',
    styleUrls: ['./recipe-spot.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RecipeHeaderComponent, RecipeIngredientsComponent, RecipeStepsComponent]
})
export class RecipeSpotComponent implements OnInit {
    public static readonly ref: string = 'recipe';

    @Input()
    public data: RecipeSpot;

    constructor(private readonly schemaService: SchemaService) {}

    ngOnInit(): void {
        const {
            recipeName,
            images,
            description,
            author,
            recipeCategory,
            cookingTimeDays,
            cookingTimeHours,
            cookingTimeMinutes,
            yieldText,
            recipeCuisine,
            calories,
            recipeYield,
            ingredientSections,
            instructionSections
        } = this.data;

        this.schemaService.setSchema<Recipe>({
            '@type': 'Recipe',
            'name': recipeName,
            ...removeEmpty<Omit<Recipe, '@type' | 'name'>>({
                image: images.map((image: UmbracoImage) => image.src).join(),
                description,
                author: author && {
                    '@type': 'Person',
                    'name': author
                },
                recipeCategory,
                totalTime: this.getIsoTime({
                    days: cookingTimeDays,
                    hours: cookingTimeHours,
                    minutes: cookingTimeMinutes
                }),
                recipeYield: this.getYield(recipeYield, yieldText),
                recipeCuisine,
                ...(calories
                    ? {
                          nutrition: {
                              '@type': 'NutritionInformation',
                              'calories': `${calories} calories`
                          }
                      }
                    : {})
            }),
            'recipeIngredient': this.getIngredients(ingredientSections),
            'recipeInstructions': this.getInstructions(instructionSections)
        });
    }

    private getInstructions(sections: RecipeInstructionSection[]): HowToSection[] {
        return sections.map((section: RecipeInstructionSection) => ({
            '@type': 'HowToSection',
            'name': section.instructionHeading,
            'itemListElement': section.instructionList.map((step: RecipeInstructionItem) => ({
                '@type': 'HowToStep',
                'text': step.text
            }))
        }));
    }

    private getIngredients(sections: RecipeIngredientSection[]): string[] {
        const ingredients = sections.map(section => section.ingredientsList).flat(1);
        return ingredients.map(({ amount, unit, ingredientName }) => {
            const filteredValues = {
                ...(amount ? { amount } : {}),
                ...(unit ? { unit } : {}),
                ingredientName
            };
            return Object.values(filteredValues).join(' ');
        });
    }

    private getYield(
        yieldNumber: number | undefined,
        text: string | undefined
    ): string | QuantitativeValue | (string | QuantitativeValue)[] {
        if (yieldNumber && text) {
            return [(yieldNumber as unknown) as QuantitativeValue, text];
        }
        return text || '';
    }

    private getIsoTime(time: RecipeTime): string {
        const { days, hours, minutes } = time;
        if (!days && !hours && !minutes) {
            return '';
        }
        return `P${time.days ? time.days + 'D' : ''}T${time.hours ? time.hours + 'H' : ''}${
            time.minutes ? time.minutes + 'M' : ''
        }`;
    }
}
